import React, { useEffect, useState } from "react";
import { styled } from "styled-components";
import PageBanner from "../components/PageBanner";

const EFFECTIVE_DATE = "15 October 2026";
const LAST_UPDATED = "15 October 2026";

/*
 * Policy content.
 * Each section has an id (used for the side menu / anchor links) and one or more blocks.
 * A block can have: title, paragraphs [], list [], and closing paragraphs (after) [].
 * If a section has a single block, its title is not shown (the section heading is enough).
 */
const sections = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    blocks: [
      {
        title: "1.1 Personal Identification Information",
        paragraphs: [
          "When you register, book a test or contact us, we may collect your full name, mobile number, email address, date of birth or age, gender where required for diagnostic services, address or location details where needed, account login information, and details of a parent, guardian, caregiver or other authorised representative where applicable.",
        ],
      },
      {
        title: "1.2 Account and Registration Information",
        paragraphs: [
          "When you create or access a Konnect account, we may collect registration details, account identifiers, authentication information, login activity, preferences and information associated with your account. Your account may allow you to manage bookings, view booking history, access available diagnostic reports and update personal details. You are responsible for maintaining the confidentiality of your login credentials and notifying us of suspected unauthorised access.",
        ],
      },
      {
        title: "1.3 Diagnostic Booking Information",
        paragraphs: [
          "When you book a diagnostic test, health package, laboratory service, imaging service or related appointment, we may collect selected tests or packages, appointment date and time, preferred diagnostic centre or service location, patient details, home sample collection address where applicable, prescription or referring physician details where required, booking confirmations, cancellations, rescheduling requests, service history, payment status, invoices and refund-related information.",
        ],
      },
      {
        title: "1.4 Health and Diagnostic Information",
        paragraphs: [
          "To provide diagnostic services, we may process health-related information such as prescriptions and relevant medical history provided to us, test results and laboratory findings, radiology or imaging information, diagnostic reports and associated clinical information. We use this information to facilitate diagnostic services, prepare and deliver reports, support authorised healthcare professionals and fulfil legal and operational responsibilities.",
          "Health-related information is handled with appropriate confidentiality and access restrictions. Please provide only information necessary for the service requested.",
        ],
      },
      {
        title: "1.5 Payment Information",
        paragraphs: [
          "Where online payments are supported, transactions may be processed through third-party payment service providers. We may receive transaction references, payment status, payment method type and billing details needed to confirm bookings, process refunds and maintain financial records. Unless expressly required and securely supported by our payment infrastructure, we do not intend to store complete payment card details or payment authentication credentials on our own systems.",
        ],
      },
      {
        title: "1.6 Technical and Website Usage Information",
        paragraphs: [
          "When you visit our website, certain technical information may be collected automatically, including:",
        ],
        list: [
          "IP address",
          "Browser type, device type and operating system",
          "Pages visited and interactions with website features",
          "Date and duration of visits",
          "Referring website or traffic source",
          "Cookie identifiers or similar technical data where enabled",
        ],
      },
      {
        title: "1.7 Communications and Customer Support",
        paragraphs: [
          "If you contact us by telephone, email, website forms or other available channels, we may collect your name, contact details, enquiry, feedback, complaint and related correspondence to respond to you, resolve disputes and improve our services.",
        ],
      },
    ],
  },
  {
    id: "how-we-use",
    title: "2. How We Use Your Information",
    blocks: [
      {
        title: "Account creation and management",
        list: [
          "Register and authenticate users",
          "Maintain account profiles and preferences",
          "Enable access to booking history and available reports",
          "Support account recovery and security",
        ],
      },
      {
        title: "Diagnostic bookings and service delivery",
        list: [
          "Process, confirm and manage bookings",
          "Schedule appointments and coordinate diagnostic centres or sample collection",
          "Communicate appointment reminders, changes and cancellations",
          "Facilitate testing and delivery of reports",
          "Coordinate with authorised healthcare professionals and relevant service providers",
        ],
      },
      {
        title: "Payments and administration",
        list: [
          "Confirm payments and process eligible refunds",
          "Generate invoices and receipts",
          "Maintain accounting and transaction records",
          "Address booking disputes and payment enquiries",
        ],
      },
      {
        title: "Customer support and service improvement",
        list: [
          "Respond to queries, feedback and complaints",
          "Improve the website, booking process and customer experience",
          "Analyse service usage and operational performance",
        ],
      },
      {
        title: "Security and legal compliance",
        list: [
          "Detect fraud, misuse and unauthorised access",
          "Protect our website, accounts and systems",
          "Maintain records required by applicable law",
          "Respond to lawful requests from authorised authorities",
          "Establish, exercise or defend legal claims where necessary",
        ],
      },
      {
        title: "Marketing and promotional communications",
        paragraphs: [
          "Where permitted by applicable law, we may send information about services, health packages, offers and updates if you have provided any required consent. You may opt out of promotional communications at any time. Opting out will not prevent essential service-related communications such as booking confirmations, payment updates or report availability notifications.",
          "We will not use diagnostic information for unrelated advertising purposes without an appropriate lawful basis and any consent required by law.",
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "3. Cookies and Similar Technologies",
    blocks: [
      {
        paragraphs: [
          "Our website may use cookies and similar technologies to keep features functioning, support account sessions and booking functionality, remember preferences, understand website performance and measure marketing effectiveness where applicable.",
          "You can manage cookies through your browser settings and, where provided, our cookie preference controls. Disabling certain cookies may affect account access, booking functionality or other features. Where required by law, we will obtain consent before using non-essential cookies or similar tracking technologies.",
        ],
      },
    ],
  },
  {
    id: "how-we-share",
    title: "4. How We Share Your Information",
    blocks: [
      {
        title: "Diagnostic centres and healthcare professionals",
        paragraphs: [
          "We may share patient details, booking information, prescriptions and relevant clinical information with diagnostic centres, laboratories, radiologists, pathologists and authorised healthcare professionals involved in delivering requested services.",
        ],
      },
      {
        title: "Technology and service providers",
        paragraphs: [
          "We may use third-party providers for website hosting, cloud infrastructure, booking management, customer support, communication services, authentication, analytics and information security. Such providers may process information on our behalf, subject to applicable contractual, confidentiality and security requirements.",
        ],
      },
      {
        title: "Payment service providers",
        paragraphs: [
          "We may share relevant transaction and billing information with payment gateways, banks and other payment service providers to process payments, confirm transactions and manage refunds.",
        ],
      },
      {
        title: "Legal and regulatory authorities",
        paragraphs: [
          "We may disclose information where required by applicable law, a valid legal process, a competent court or an authorised regulatory or governmental authority.",
        ],
      },
      {
        title: "Business transfers",
        paragraphs: [
          "If Konnect undergoes a merger, restructuring, acquisition or transfer of business assets, relevant information may be transferred as part of that transaction, subject to applicable legal requirements and appropriate safeguards.",
        ],
      },
      {
        title: "No sale of personal information",
        highlight: true,
        paragraphs: [
          "We do not sell your personal information to third parties. We do not authorise third parties to use your information for purposes unrelated to the services they provide to Konnect, except where permitted or required by law.",
        ],
      },
    ],
  },
  {
    id: "how-we-protect",
    title: "5. How We Protect Your Information",
    blocks: [
      {
        paragraphs: [
          "We implement reasonable technical, organisational and operational measures designed to protect personal information against unauthorised access, disclosure, alteration, loss or misuse. Depending on the systems and services involved, these measures may include access restrictions, authentication controls, secure data transmission, system monitoring, backups and confidentiality obligations.",
          "Access to health and diagnostic information should be limited to authorised personnel and service providers who need it to perform their responsibilities.",
          "No website, electronic transmission or storage system can be guaranteed to be completely secure. If a personal data breach occurs, we will respond and provide notifications as required by applicable law.",
        ],
      },
    ],
  },
  {
    id: "data-retention",
    title: "6. Data Retention",
    blocks: [
      {
        paragraphs: [
          "We retain personal information only for as long as reasonably necessary to fulfil the purposes for which it was collected, provide services, maintain accounts and booking records, comply with applicable legal and regulatory obligations, resolve disputes and enforce agreements.",
          "Retention periods may vary by information type:",
        ],
        list: [
          "Account information may be retained while an account is active and for an appropriate period thereafter.",
          "Booking and payment records may be retained for service administration, accounting and legal requirements.",
          "Diagnostic reports and clinical records are retained in accordance with applicable healthcare, laboratory, regulatory and legal requirements.",
          "Customer support communications are retained for an appropriate period.",
          "Technical logs are retained for security and operational purposes.",
        ],
        after: [
          "When information is no longer required, we will take appropriate steps to delete it or render it non-identifiable, subject to applicable legal obligations and legitimate retention requirements. Account deletion may not immediately remove records that we are legally required or otherwise lawfully permitted to retain.",
        ],
      },
    ],
  },
  {
    id: "your-rights",
    title: "7. Your Privacy Rights and Choices",
    blocks: [
      {
        paragraphs: ["Subject to applicable law, you may have the right to:"],
        list: [
          "Request access to information associated with your account",
          "Request correction or updating of inaccurate or incomplete personal information",
          "Request deletion where legally permissible",
          "Withdraw consent for processing that relies on your consent",
          "Opt out of promotional communications",
          "Raise a grievance or privacy-related complaint",
          "Exercise other rights available under applicable data protection laws",
        ],
        after: [
          "Contact us using the details below to submit a request. We may need to verify your identity before acting. Some information may need to be retained where required by law or necessary for services, subject to applicable legal requirements.",
          "Where you withdraw consent, we will stop relevant processing that relies on that consent, subject to lawful grounds for continued processing. Withdrawal may affect our ability to provide services that depend on the information concerned.",
        ],
      },
    ],
  },
  {
    id: "account-deletion",
    title: "8. Account Deletion and Closure",
    blocks: [
      {
        paragraphs: [
          "You may request closure or deletion of your Konnect account by contacting us using the details below or using an account deletion feature if one is available on the website. We will review and process valid requests in accordance with applicable law and retention obligations. We may ask you to verify your identity.",
          "Account closure may affect your ability to access booking history, diagnostic reports and other account-based services. Please download any reports you need before requesting deletion, where downloads are available.",
          "Information that must be retained for legal, regulatory, accounting, clinical or dispute-resolution purposes may be retained for the applicable period with appropriate safeguards.",
        ],
      },
    ],
  },
  {
    id: "third-party",
    title: "9. Third-Party Websites and Services",
    blocks: [
      {
        paragraphs: [
          "Our website may contain links to third-party websites, payment gateways or other external services. These services operate under their own privacy policies and terms. Konnect is not responsible for the privacy practices, content or security of third-party websites that we do not control. We encourage you to review their privacy policies before providing personal information.",
        ],
      },
    ],
  },
  {
    id: "childrens-privacy",
    title: "10. Children's Privacy and Information About Other Individuals",
    blocks: [
      {
        paragraphs: [
          "Our services may be used to arrange diagnostic tests for children or other individuals through a parent, legal guardian or authorised representative, where permitted. When personal information relates to a child or a person represented by another individual, we will handle it in accordance with applicable law, including requirements relating to parental or guardian consent and authorised representation.",
          "A person booking a service on behalf of another individual should provide accurate information and ensure they are authorised to submit it.",
        ],
      },
    ],
  },
  {
    id: "international-transfers",
    title: "11. International Data Transfers",
    blocks: [
      {
        paragraphs: [
          "Depending on the technology providers and infrastructure used by Konnect, personal information may be processed or stored outside the state or country in which it was collected. Where cross-border transfers occur, Konnect will take the steps required by applicable law and implement appropriate safeguards where necessary.",
        ],
      },
    ],
  },
  {
    id: "policy-changes",
    title: "12. Changes to This Privacy Policy",
    blocks: [
      {
        paragraphs: [
          'We may update this Privacy Policy to reflect changes in our services, website functionality, data handling practices or applicable legal requirements. The revised Policy will be published on this page with an updated "Last Updated" date. Where required by law, we will provide additional notice or obtain consent before implementing changes that require it. We encourage you to review this page periodically.',
        ],
      },
    ],
  },
];

const CONTACT_ID = "contact-us";
const menuItems = [
  ...sections.map(({ id, title }) => ({ id, title })),
  { id: CONTACT_ID, title: "13. Contact Us" },
];

function PrivacyPolicy() {
  const [activeId, setActiveId] = useState(menuItems[0].id);

  // Highlight the section currently in view in the side menu
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    menuItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Wrapper className="privacy-policy">
      {/* Swap bannerImg for a dedicated privacy banner class once one is added */}
      <PageBanner title={"Privacy Policy"} bannerImg={"contact-us-bg-image"} />

      <div className="container px-md-0 px-4 mx-auto my-5">
        <div className="row g-4">
          {/* Side menu (desktop only) */}
          <aside className="col-lg-3 d-none d-lg-block">
            <nav className="toc bg_light2 shadow-sm rounded p-4">
              <h6 className="text_accent fw-bold mb-3">On this page</h6>
              <ul className="list-unstyled m-0">
                {menuItems.map(({ id, title }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      onClick={(e) => scrollTo(e, id)}
                      className={activeId === id ? "active" : ""}
                    >
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Policy content */}
          <div className="col-lg-9">
            <div className="policy-card rounded p-md-5 p-4">
              <p className="dates text-k-text mb-4">
                <span className="fw-bold">Effective Date:</span>{" "}
                {EFFECTIVE_DATE}
                <span className="divider">|</span>
                <span className="fw-bold">Last Updated:</span> {LAST_UPDATED}
              </p>

              <p className="intro text-k-text">
                This Privacy Policy explains how Konnect Diagnostics ("Konnect",
                "we", "our" or "us") collects, uses, stores, discloses and
                protects your information when you visit{" "}
                <a href="https://konnectdiagnostics.com/">
                  https://konnectdiagnostics.com/
                </a>
                , create an account, book diagnostic tests or health packages,
                access diagnostic reports, contact us, or use related services.
                Where consent is required by applicable law, we will seek it
                before processing personal information for the relevant purpose.
              </p>

              {sections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="policy-section"
                >
                  <h2 className="text_accent fw-bold">{section.title}</h2>

                  {section.blocks.map((block, i) => (
                    <div
                      key={i}
                      className={`policy-block ${block.highlight ? "highlight rounded" : ""}`}
                    >
                      {block.title && section.blocks.length > 1 && (
                        <h3 className="fw-bold">{block.title}</h3>
                      )}
                      {block.paragraphs?.map((p, j) => (
                        <p key={j} className="text-k-text">
                          {p}
                        </p>
                      ))}
                      {block.list && (
                        <ul className="text-k-text">
                          {block.list.map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {block.after?.map((p, j) => (
                        <p key={j} className="text-k-text">
                          {p}
                        </p>
                      ))}
                    </div>
                  ))}
                </section>
              ))}

              {/* 13. Contact Us */}
              <section id={CONTACT_ID} className="policy-section mb-0">
                <h2 className="text_accent fw-bold">13. Contact Us</h2>
                <p className="text-k-text">
                  If you have questions about this Privacy Policy, wish to
                  exercise your privacy rights or want to raise a concern about
                  how your information is handled, please contact us. Include
                  sufficient details for us to understand and respond to your
                  concern. Avoid sending unnecessary medical information in
                  general enquiries.
                </p>

                <div className="contact-box rounded p-md-5 p-4 mt-4">
                  <img
                    src="/images/konnect-logo.png"
                    className="mb-3"
                    alt="konnect-diagnostics"
                  />
                  <p className="text-k-text">
                    <span className="fw-bold"> Registered Office: </span>
                    1-5-1055/87/236 & 237, HMT Officers Colony, Batukhamma Park,
                    Alwal, Hyderabad, Secunderabad, Telangana – 500010, India.
                  </p>
                  <p className="text-k-text">
                    <span className="fw-bold"> Email: </span>
                    <a href="mailto:info@konnectdiagnostics.com">
                      info@konnectdiagnostics.com
                    </a>
                  </p>
                  <p className="text-k-text">
                    <span className="fw-bold"> Customer Care No: </span>
                    <a href="tel:04041235555">040 - 4123 5555</a>
                  </p>
                  <p className="text-k-text mb-0">
                    <span className="fw-bold"> Website: </span>
                    <a href="https://konnectdiagnostics.com/">
                      https://konnectdiagnostics.com/
                    </a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

export default PrivacyPolicy;

const Wrapper = styled.div`
  .toc {
    position: sticky;
    top: 110px;
    max-height: calc(100vh - 140px);
    overflow-y: auto;

    li a {
      display: block;
      padding: 7px 0 7px 12px;
      border-left: 2px solid transparent;
      color: #555;
      font-size: 14px;
      line-height: 1.4;
      text-decoration: none;
      transition:
        color 0.2s,
        border-color 0.2s;

      &:hover {
        color: #005ead;
      }

      &.active {
        color: #005ead;
        font-weight: 600;
        border-left-color: #005ead;
      }
    }
  }

  .policy-card {
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
  }

  .dates {
    font-size: 15px;

    .divider {
      margin: 0 10px;
      color: #ccc;
    }
  }

  .intro {
    font-size: 17px;
    line-height: 1.8;
    padding-bottom: 28px;
    margin-bottom: 36px;
    border-bottom: 1px solid #e9ecef;
  }

  .policy-section {
    scroll-margin-top: 110px;
    margin-bottom: 40px;

    h2 {
      font-size: 24px;
      margin-bottom: 18px;
    }
  }

  .policy-block {
    margin-bottom: 22px;

    h3 {
      font-size: 18px;
      color: #222;
      margin-bottom: 8px;
    }

    p,
    li {
      line-height: 1.8;
    }

    ul {
      padding-left: 22px;
      margin-bottom: 12px;

      li {
        margin-bottom: 4px;
      }

      li::marker {
        color: #005ead;
      }
    }

    &.highlight {
      background-color: #eef5fc;
      border-left: 4px solid #005ead;
      padding: 18px 22px;

      p:last-child {
        margin-bottom: 0;
      }
    }
  }

  a {
    color: #005ead;
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }

  .contact-box {
    background-image: url("/images/k-10.png");
    background-position: top center;
    background-size: cover;
    background-repeat: no-repeat;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;

    img {
      max-width: 260px;
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    .policy-section h2 {
      font-size: 20px;
    }

    .intro {
      font-size: 16px;
    }
  }
`;
